import { useSelector } from 'react-redux';
import type {
  EmergencyCall,
  EmergencyCallLocation,
  EmergencyConversationLocation,
} from '@rsos/reduxDataStore/src/features/emergencyCalls/emergencyCalls.types';
import {
  apiTranscriptionToStateCall,
  MAX_CALL_START_TIME_OFFSET,
} from './transcriptionAdapter';
import type { RootState } from './transcriptionRootState';
import type { TranscriptionStateCall } from './transcriptionSlice.types';

/**
 * Returns the best transcription object for the currently selected call.
 */
export function useTranscriptionForSelectedCall() {
  return useSelector((state: RootState) => {
    const callID = state.emergencyCalls.selectedCallerID;
    return selectTranscriptionForCall(callID)(state);
  });
}

export function useAllKeywordMatches() {
  return useTranscriptionForSelectedCall()?.keyword_matches || [];
}

export const useSelectedKeywordMatch = () => {
  const matches = useAllKeywordMatches();
  const match = useTranscriptionForSelectedCall()?.selectedKeywordMatch;
  const matchSet = matches.find(m => m.keyword_set.id === match?.keywordSetID);
  if (!match || !matchSet) {
    return undefined;
  }
  return {
    matchIndex: match?.matchIndex,
    keywordSet: matchSet,
  };
};

function callStartTime(call?: EmergencyCall) {
  const firstLocation = (call?.core?.locations || [])[0];

  // Calls from search don't have locations or start times; a zero epoch
  // default allows the latest transcript we have to show up in such cases.
  return (
    (firstLocation as EmergencyConversationLocation)?.conversation_start_time ||
    (firstLocation as EmergencyCallLocation)?.call_start_time ||
    0
  );
}

const noTranscript = apiTranscriptionToStateCall(null);

/**
 * Given a callID, returns a selector. The selector takes root state and returns an
 * appropriate transcription if there is one, or an empty transcription if nothing
 * corresponds to this call.
 *
 * Uses the call_start_time to filter out transcripts for old calls.
 */
export function selectTranscriptionForCall(callID: string | null) {
  return (state: RootState): TranscriptionStateCall => {
    if (!callID) return noTranscript;
    const transcription = state.transcriptions?.calls[callID];
    const call = state.emergencyCalls.callQueue[callID];
    const startTime = callStartTime(call);

    const transcriptionStartTime =
      transcription?.payload?.call_information?.call_start_time || startTime;

    if (startTime - transcriptionStartTime < MAX_CALL_START_TIME_OFFSET) {
      return transcription || noTranscript;
    }
    return noTranscript;
  };
}

export function selectPreviousTranscriptionsForCall(callID: string | null) {
  return (state: RootState): TranscriptionStateCall[] => {
    if (!callID) return [];

    const transcriptions =
      state.transcriptions?.transcriptionLists?.[callID] || [];

    // For the "Recent transcripts" pane, prefer excluding the current call
    // by digi_call_id instead of time-window heuristics.
    //
    // rtt_egress can return multiple transcriptions close in time; users expect
    // to see all of them in history. The 10-minute heuristic remains in
    // `selectTranscriptionForCall()` to protect the main live transcript view.
    const currentDigiCallId =
      state.transcriptions?.calls?.[callID]?.payload?.call_information
        ?.digi_call_id || '';

    const previousApi = currentDigiCallId
      ? transcriptions.filter(
          t => (t?.call_information?.digi_call_id || '') !== currentDigiCallId,
        )
      : transcriptions;

    // Transcriptions are already stored sorted by start_time desc, but keep it safe.
    const sorted = [...previousApi].sort((a, b) => {
      const bt = Number(b?.call_information?.call_start_time || 0);
      const at = Number(a?.call_information?.call_start_time || 0);
      return bt - at;
    });

    return sorted.map(t => apiTranscriptionToStateCall(t));
  };
}
